import moment from 'moment';

import { requestApi } from '@app/api';
import { withDocument } from '@app/api/paths';
import INITIAL_FILTER_VALUES from '@app/constants/documentsFilterInitialValues';
import { ScheduledDocumentFailure } from '@app/features/documents/types';
import { ReminderFormState } from '@app/features/reminder/types';
import splitToSubstrByLastDelimiter from '@app/libs/splitToSubstrByLastDelimiter';
import AllowSignatureTypes from '@app/store/AllowSignatureTypes';
import { CarbonCopyContact } from '@app/store/contract-setup/reducers/carbonCopies';
import DateFilterType from '@app/store/documents/DateFilterType';
import SearchType from '@app/store/documents/SearchType';
import StatusFilter from '@app/store/documents/StatusFilter';
import { RequesterAttachment } from '@app/store/documentSetup/RequesterAttachment';
import Host, { HostReference } from '@app/store/Host';
import LocaleCode from '@app/store/LocaleCode';
import TextAlign from '@app/store/TextAlign';
import { convertTransportHostsToAppHosts } from '@app/store/transport-layer/lib/host';
import { Abort } from '@app/store/transport-layer/types/abort';
import { Attachment as TransportLayerAttachment } from '@app/store/transport-layer/types/attachment';
import { Field as TransportLayerField } from '@app/store/transport-layer/types/field';
import { FieldGroup as TransportLayerFieldGroup } from '@app/store/transport-layer/types/fieldGroup';
import {
  HostWithSigningProperties,
  Locale,
  ParticipantReference,
  ParticipantType,
  ParticipantWithSigningProperties,
  SignerWithSigningProperties,
  SigningMethod as TransportSigningMethod,
} from '@app/store/transport-layer/types/participant';
import { WithId } from '@app/store/transport-layer/types/utility';
import {
  Verification as TransportLayerVerification,
  VerificationMethod as TransportLayerVerificationMethod,
} from '@app/store/transport-layer/types/verification';

import { getDocumentsParticipants, GetDocumentsParticipantsResponse } from './participants';

interface CreationInfo {
  requestMethod: {
    path: string;
  };
}

export interface Label {
  id: string;
  workspaceId: string;
  name: string;
  resourceType: 'DOCUMENT';
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Participant {
  name: string;
  participantId: string;
  signingOrder: number;
  signingExpiryDate: SigningExpiryDate;
  signingMethod: SigningMethod;
  locale?: LocaleCode;
  host?: HostReference;
}

interface SigningExpiryDate {
  date: string | null;
  isValid: boolean;
}

interface SigningMethod {
  methodType: string;
  contactInfo: string;
}

interface Pending {
  participantId: string | null;
  pendedAt: string;
  pendingType: string;
  reason: string;
}

interface Requester {
  email: string;
  name: string;
}

interface Signing {
  participantId: string;
  signedAt: string;
  expiredAt: string | null;
}

interface DocumentSchedule {
  scheduledDate: string;
  failure: ScheduledDocumentFailure | null;
}

export interface Document {
  createdAt: string;
  creationInfo: CreationInfo;
  currentSigningOrder: number;
  documentId: string;
  originHash: string | null;
  labels: Label[];
  isAbleToBeRead: boolean;
  isHidden: boolean;
  name: string;
  participants: Participant[];
  hosts: Host[];
  pending: Pending | null;
  requester: Requester;
  signings: Signing[];
  status: string;
  schedule: DocumentSchedule | null;
  finishedAt: string | null;
  updatedAt: string;
}

export type DocumentListItemType = Document & {
  attachmentsCount: number;
  needAgreement: boolean;
  file: string | null;
}; // TODO(@mgahn0706): openapi-client를 사용하여 Document Type 관리하기
export interface DocumentsResponse {
  countOfDocuments: number;
  documents: DocumentListItemType[];
}

export type GetDocumentsParam = {
  assigned?: boolean | null;
  dateFilterType?: DateFilterType;
  dateRangeFrom?: string | null;
  dateRangeTo?: string | null;
  limit?: number;
  labelId?: string | null;
  isArchived?: boolean;
  offset?: number;
  requestMethod?: string;
  searchKeyword?: string;
  groupId?: string | null;
  searchType?: SearchType;
  status?: StatusFilter;
  sort?: string;
};

interface NewGetDocumentsParams {
  offset?: number;
  createdAt?: {
    le?: string;
    ge?: string;
  };
  updatedAt?: {
    le?: string;
    ge?: string;
  };
  finishedAt?: {
    le?: string;
    ge?: string;
  };
  limit?: number;
  isArchived?: boolean;
  assigned?: boolean | null;
  labelId?: string | null;
  title?: string;
  groupId?: string | null;
  participantType?: ParticipantType;
  participantNameOrContact?: string;
  status?: Uppercase<StatusFilter>;
  requestMethod?: string;
  /*
      Unused property but API spec defines the property.
  */
  includeMetadata?: boolean;
  keywords?: string;
  isHidden?: boolean;
  sort?: string;
}

export interface NewDocument {
  id: string;
  originHash: string | null;
  title: string;
  status: string;
  labels: Label[];
  carbonCopies: CarbonCopyContact[];
  currentSigningOrder: number;
  signings: NewSigning[];
  participants: WithId<ParticipantWithSigningProperties>[];
  abort: Abort | null;
  blockSignerAccess: boolean;
  isHidden: boolean;
  file: { url: string } | null;
  auditTrail: { url: string } | null;
  createdAt: string;
  updatedAt: string;
  schedule: DocumentSchedule | null;
  finishedAt: string | null;
  /*
    Unused property but API spec defines the property.
    We should define this as common type when we needed.
    FYI: https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#ac63e80598e6495386765a85b5c65a14
  */
  keywords: unknown[];
}

type NewSigning = {
  participant: ParticipantReference;
  signedAt: string;
  completedAt: string | null;
  expiredAt: string | null;
};

export type NewDocumentById = NewDocument & {
  termsOfUseId: string | null;
  finishedAt: string | null;
};

export type NewDocumentForGetDocuments = NewDocument & {
  creationInfo: CreationInfo;
  participants: WithId<ParticipantWithSigningProperties>[];
  attachments: { count: number };
  needAgreement: boolean;
};

export interface NewDocumentsResponse {
  documents: NewDocumentForGetDocuments[];
  count: number;
}

function convertParticipantTypeParam(searchType?: SearchType): 'SIGNER' | 'REQUESTER' | undefined {
  switch (searchType) {
    case SearchType.SIGNER:
      return 'SIGNER';
    case SearchType.WRITER:
      return 'REQUESTER';
    default:
      return undefined;
  }
}

const convertToNewRequestForGetDocuments = (params: GetDocumentsParam): NewGetDocumentsParams => {
  const {
    assigned,
    limit,
    offset,
    labelId,
    searchType,
    searchKeyword,
    requestMethod,
    status,
    groupId,
    sort,
    isArchived,
    dateFilterType,
    dateRangeFrom,
    dateRangeTo,
  } = params;
  const hasDateRange = dateRangeFrom || dateRangeTo;
  const dateRange = {
    ge: dateRangeFrom || undefined,
    le: dateRangeTo || undefined,
  };

  return {
    assigned,
    createdAt: dateFilterType === 'CREATED_AT' && hasDateRange ? dateRange : undefined,
    updatedAt: dateFilterType === 'UPDATED_AT' && hasDateRange ? dateRange : undefined,
    finishedAt: dateFilterType === 'FINISHED_AT' && hasDateRange ? dateRange : undefined,
    limit,
    title: searchType === SearchType.TITLE && searchKeyword ? searchKeyword : undefined,
    labelId,
    offset,
    groupId,
    participantType: convertParticipantTypeParam(searchType),
    participantNameOrContact:
      searchType === SearchType.SIGNER || searchType === SearchType.WRITER
        ? searchKeyword
        : undefined,
    status:
      status === INITIAL_FILTER_VALUES.status
        ? undefined
        : (status?.toUpperCase() as Uppercase<StatusFilter>),
    requestMethod,
    isHidden: isArchived,
    sort,
  };
};

const convertToDeprecatedDocumentsResponse = (response: NewDocumentsResponse) => {
  return {
    countOfDocuments: response.count,
    documents: response.documents.map((document) => ({
      creationInfo: document.creationInfo,
      attachmentsCount: document.attachments.count,
      needAgreement: document.needAgreement,
      file: document.file ? document.file.url : null,
      ...convertToDeprecatedDocumentsInternal(document, document.participants),
    })),
  };
};

/**
 *
 * @deprecated Endpoint response and request are modified and splitted in standardize-document-content
 * @see https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#32124969aafb48989b17ae23599aa8eb
 */
export function getDocuments(
  workspaceId: string,
  params: GetDocumentsParam
): Promise<DocumentsResponse> {
  return requestApi<NewDocumentsResponse>({
    url: withDocument(`/workspaces/${workspaceId}/documents`),
    method: 'GET',
    credentials: true,
    params: convertToNewRequestForGetDocuments(params),
  }).then((response) => convertToDeprecatedDocumentsResponse(response.data));
}

export type DocumentDetail = Omit<Document, 'creationInfo'> & {
  carbonCopies: CarbonCopyContact[];
  auditTrailFile: string | null;
};

export interface DocumentsByIdResponse {
  result: {
    document: DocumentDetail;
  };
}

const convertToDeprecatedDocumentsByIdResponse = (
  document: NewDocumentById,
  participant: GetDocumentsParticipantsResponse
): DocumentDetail => {
  return convertToDeprecatedDocumentsInternal(document, participant.participants) as DocumentDetail;
};
/**
 *
 * @deprecated Endpoint is modified and splitted in standardize-document-content
 * @see https://www.notion.so/modusign/bb8e24e723434ecc946dfecb85db5ee3#4763c90df7eb4bf697f7a1e83e0a02fc
 */
export function getDocumentsByIdDeprecated({
  documentId,
  workspaceId,
}: {
  documentId: string;
  workspaceId: string;
}): Promise<DocumentDetail> {
  return Promise.all([
    getDocumentsById({ documentId, workspaceId }),
    getDocumentsParticipants({ documentId, workspaceId }),
  ]).then((values) => convertToDeprecatedDocumentsByIdResponse(values[0], values[1]));
}

export function getDocumentsById({
  documentId,
  workspaceId,
}: {
  documentId: string;
  workspaceId: string;
}): Promise<NewDocumentById> {
  return requestApi<NewDocumentById>({
    url: withDocument(`/workspaces/${workspaceId}/documents/${documentId}`),
    method: 'GET',
    bypass401: true,
    credentials: true,
  }).then((response) => response.data);
}
interface CheckboxField {
  dataLabel: string;
  description: string | null;
  fieldType: string;
  locationPage: number;
  locationX: number;
  locationY: number;
  locationZ: number;
  required: boolean;
  sizeHeight: number | null;
  sizeWidth: number | null;
  groupLabel: string | null;
}

interface SignatureField {
  allowSignatureTypes: AllowSignatureTypes;
  dataLabel: string;
  description: string | null;
  fieldType: string;
  locationPage: number;
  locationX: number;
  locationY: number;
  locationZ: number;
  required: boolean;
}

interface TextField {
  dataLabel: string;
  description: string | null;
  fieldType: string;
  locationPage: number;
  locationX: number;
  locationY: number;
  locationZ: number;
  required: boolean;
  sizeHeight: number | null;
  sizeWidth: number | null;
  textStyleAlign: TextAlign;
  textStyleFontSize: number | null;
  textStyleFontType: string | null;
}

export type Field = CheckboxField | SignatureField | TextField;

export interface Authentication {
  authType: 'D_CERT' | 'MOBILE_IDENTIFICATION';
  authGuide: string;
  authValue: string;
}

export interface PasswordAuthentication {
  authType: 'PASSWORD';
  authGuide: string | null;
  authValue: string;
}

export interface Attachment {
  attachmentType: string;
  message: string | null;
  required: boolean;
}

export interface ParticipantValue {
  attachments: Attachment[];
  // TODO(@GilHyeon): API팀 인터페이스 변경여부 확인 필요
  authentications: Array<Authentication | PasswordAuthentication>;
  contactInfo: string;
  fields: TransportLayerField['ToSend'][];
  fieldGroups: TransportLayerFieldGroup[];
  locale?: string;
  messageFromRequester: string | null;
  name: string;
  signingOrder: number;
  signingMethodType: string;
  signingExpiryPeriod: number | null;
  // TODO: (standardize-document-content): Field의 participant dataLabel을 위해 선언되었습니다. 추후에 확인하고 정리해야 합니다.
  dataLabel: string;
  host?: HostReference;
}

interface RequesterInputPosition {
  x: number;
  y: number;
  z: number;
  page: number;
}

interface RequesterInputSize {
  width: number;
  height: number;
}

interface RequesterInputTextStyle {
  align: TextAlign;
  fontType: string;
  fontSize: number;
}

export interface RequesterInput {
  checkboxInputChecked: boolean | null;
  dataLabel: string;
  description: string | null;
  file: {
    id: string;
    otp: string;
  } | null;
  inputValueType: string;
  position: RequesterInputPosition;
  size: RequesterInputSize | null;
  textInputContent: string | null;
  textInputTextStyle: RequesterInputTextStyle | null;
}

interface PostDocumentsResponse {
  documentId: string;
}

export interface NewPostDocumentsResponse {
  id: string;
}

export interface RequestMethod {
  // (2022.10.28) draft와 test만 서버에서 허용됩니다.
  // 참고: https://github.com/modusign/modusign-backend-app/pull/5755
  path: `/drafts/${string}` | `/users/${string}/test`;
}

export interface RoleGrant {
  subjectType: 'MEMBER' | 'GROUP';
  subjectId: string;
  name: string;
}

export interface PostDocumentsParam {
  carbonCopies?: CarbonCopyContact[];
  fileId: string;
  fileOpenPassword: string | null;
  locale?: string;
  participants: ParticipantValue[];
  hosts?: Host[];
  keywords?: Array<string>;
  requesterAttachments: RequesterAttachment[];
  requestMethod: RequestMethod | null;
  requesterFields: TransportLayerField['ToSend'][];
  requesterReference: ParticipantReference;
  title: string;
  labelIds?: string[];
  scheduledDate: string | null;
  roleGrants?: RoleGrant[];
}

export interface NewPostDocumentsParams {
  title: string;
  file: {
    id: string;
  };
  pdfPassword: string | null;
  participants: ParticipantWithSigningProperties[];
  attachments: TransportLayerAttachment['ToSend'][];
  fields: TransportLayerField['ToSend'][];
  fieldGroups: TransportLayerFieldGroup[];
  verifications: TransportLayerVerification[];
  carbonCopies?: CarbonCopyContact[];
  requestMethod: RequestMethod | null;
  keywords?: Array<string>;
  labelIds?: string[];
  scheduledDate: string | null;
  roleGrants?: RoleGrant[];
  reminders?: ReminderFormState[];
}

export const convertToDeprecatedPostDocumentResponse = (
  response: NewPostDocumentsResponse
): PostDocumentsResponse => {
  return {
    documentId: response.id,
  };
};

const checkAuthenticationTypeValid = (
  participant: ParticipantValue,
  authType: 'PASSWORD' | 'MOBILE_IDENTIFICATION' | 'D_CERT'
) => {
  switch (authType) {
    case 'D_CERT':
      if (participant.signingMethodType === 'IN_PERSON') {
        throw new Error(`${participant.signingMethodType} dose not support ${authType}`);
      }
      break;
    case 'PASSWORD':
    case 'MOBILE_IDENTIFICATION':
      break;
    default:
      throw new Error(`${authType} is not supported`);
  }
};

// Exported for POST api/document
export const convertToNewPostDocumentsRequestParams = (
  params: PostDocumentsParam
): NewPostDocumentsParams => {
  const convertSigningMethodType = (type: string): TransportSigningMethod => {
    switch (type) {
      case 'email':
        return 'EMAIL';
      case 'kakao':
        return 'KAKAO';
      case 'SECURE_LINK':
        return 'SECURE_LINK';
      case 'IN_PERSON':
        return 'IN_PERSON';
      default:
        throw new Error(`${type} is not supported`);
    }
  };
  const signers: SignerWithSigningProperties[] = [];
  const attachments: TransportLayerAttachment['ToSend'][] = [];
  const verifications: TransportLayerVerification[] = [];
  const fields: TransportLayerField['ToSend'][] = [];
  const fieldGroups: TransportLayerFieldGroup[] = [];
  const hosts: HostWithSigningProperties[] =
    params.hosts?.map<HostWithSigningProperties>((h) => {
      return {
        type: 'HOST',
        dataLabel: h.dataLabel,
        contact: h.contactInfo,
        name: h.name,
        locale: h.locale,
      };
    }) ?? [];

  attachments.push(
    ...params.requesterAttachments.map<TransportLayerAttachment['ToSend']>((file) => {
      return {
        title: file.file.name,
        message: null,
        config: { required: true },
        participant: {
          id: params.requesterReference.id,
          dataLabel: params.requesterReference.dataLabel,
        },
        input: {
          file: {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            fileId: file.uploadedFile!.fileId,
            fileName: file.file.name,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            token: file.uploadedFile!.token,
          },
        },
      };
    })
  );

  params.participants.forEach((participant) => {
    participant.fields.forEach((f) => fields.push(f));

    participant.fieldGroups.forEach((fg) => fieldGroups.push(fg));

    participant.attachments.forEach((attachment) => {
      attachments.push({
        title: attachment.attachmentType,
        message: attachment.message,
        config: {
          required: attachment.required,
        },
        participant: {
          dataLabel: participant.dataLabel,
        },
        input: null,
      });
    });

    verifications.push({
      participant: {
        dataLabel: participant.dataLabel,
      },

      methods: participant.authentications.map<TransportLayerVerificationMethod>((auth) => {
        checkAuthenticationTypeValid(participant, auth.authType);

        const commonPart = {
          hint: null,
          passValue: auth.authValue,
        };
        const splittedGuide = splitToSubstrByLastDelimiter(auth.authGuide || '', '|');

        switch (auth.authType) {
          case 'PASSWORD':
            return {
              ...commonPart,
              hint: auth.authGuide || null,
              type: 'PASSWORD',
            };
          case 'MOBILE_IDENTIFICATION':
            return {
              ...commonPart,
              displayName: splittedGuide[0] || '',
              displayPhoneNumber: splittedGuide[1] || '',
              type: 'MOBILE_IDENTIFICATION',
            };
          case 'D_CERT':
            return {
              ...commonPart,
              displayName: splittedGuide[0] || '',
              displayBizNumber: splittedGuide[1] || '',
              type: 'D_CERT',
            };
          default:
            throw new Error(`${auth} is not supported`);
        }
      }),
    });

    signers.push({
      type: 'SIGNER',
      name: participant.name,
      contact: participant.contactInfo,
      dataLabel: participant.dataLabel,
      order: participant.signingOrder,
      signingMethod: convertSigningMethodType(participant.signingMethodType),
      signingPeriod: null,
      signingDuration: participant.signingExpiryPeriod || 20160,
      locale: participant.locale === 'en' ? 'en' : 'ko',
      message: participant.messageFromRequester,
      host: participant.host,
    });
  });

  return {
    title: params.title,
    file: { id: params.fileId },
    pdfPassword: params.fileOpenPassword || null,
    attachments,
    verifications,
    participants: [
      ...signers,
      ...hosts,
      {
        type: 'REQUESTER',
        dataLabel: params.requesterReference.dataLabel,
      },
    ],
    fields: [...fields, ...params.requesterFields],
    fieldGroups,
    carbonCopies: params.carbonCopies,
    keywords: params.keywords,
    requestMethod: params.requestMethod,
    labelIds: params.labelIds,
    scheduledDate: params.scheduledDate,
    roleGrants: params.roleGrants,
  };
};

/**
 * @deprecated
 */
export function postDocuments(
  workspaceId: string,
  params: PostDocumentsParam
): Promise<{
  documentId: string;
}> {
  return postNewDocuments(workspaceId, convertToNewPostDocumentsRequestParams(params)).then(
    (data) => convertToDeprecatedPostDocumentResponse(data)
  );
}

export function postNewDocuments(workspaceId: string, params: NewPostDocumentsParams) {
  return requestApi<NewPostDocumentsResponse>({
    url: withDocument(`/workspaces/${workspaceId}/documents`),
    method: 'POST',
    data: params,
    credentials: true,
  }).then((response) => response.data);
}

export function hideDocuments({
  workspaceId,
  data,
}: {
  workspaceId: string;
  data: { documentIds: string[] };
}) {
  return requestApi({
    url: withDocument(`/workspaces/${workspaceId}/documents/hide`),
    method: 'POST',
    credentials: true,
    data,
  });
}

export function showDocuments({
  workspaceId,
  data,
}: {
  workspaceId: string;
  data: { documentIds: string[] };
}) {
  return requestApi({
    url: withDocument(`/workspaces/${workspaceId}/documents/show`),
    method: 'POST',
    credentials: true,
    data,
  });
}

// GetDocumentById에는 creationInfo가 존재하지 않기 때문에 이를 제외하고 매핑하는 함수입니다.
const convertToDeprecatedDocumentsInternal = (
  document: NewDocumentById | NewDocument,
  participants: WithId<ParticipantWithSigningProperties>[]
): DocumentDetail | Omit<Document, 'creationInfo'> => {
  const abortToPending = (
    abort: Abort,
    participants: WithId<ParticipantWithSigningProperties>[]
  ): Pending => {
    const participant = participants.find((p) => p.dataLabel === abort.participant.dataLabel);
    const isAbortedFromRequester = participant && participant.type === 'REQUESTER';

    return {
      participantId: isAbortedFromRequester ? null : (abort.participant.id as string),
      pendedAt: abort.abortedAt,
      pendingType: abort.type.toLocaleLowerCase(),
      reason: abort.message as string,
    };
  };

  const toLocaleCode = (locale: Locale): LocaleCode => {
    if (locale === 'en') {
      return LocaleCode.ENGLISH;
    }
    return LocaleCode.KOREAN;
  };

  const convertSigningMethod = (signingMethod: TransportSigningMethod): string => {
    if (signingMethod === 'SECURE_LINK' || signingMethod === 'IN_PERSON') {
      return signingMethod as string;
    }

    return signingMethod.toLowerCase() as string;
  };

  const signingMapper = (signing: NewSigning): Signing => {
    return {
      participantId: signing.participant.id || signing.participant.dataLabel,
      signedAt: signing.signedAt,
      expiredAt: signing.expiredAt,
    };
  };

  const requester = participants.find(
    (p) => p.type === 'REQUESTER'
  ) as WithId<ParticipantWithSigningProperties>;

  const hosts = convertTransportHostsToAppHosts(
    participants.filter((p): p is WithId<HostWithSigningProperties> => p.type === 'HOST')
  );

  const convertStatus = (status: string) => {
    if (status === 'ABORTED') {
      return 'pending';
    }
    return status.toLowerCase();
  };

  return {
    auditTrailFile: document.auditTrail ? document.auditTrail.url : null,
    name: document.title,
    documentId: document.id,
    carbonCopies: document.carbonCopies,
    createdAt: document.createdAt,
    currentSigningOrder: document.currentSigningOrder,
    labels: document.labels,
    originHash: document.originHash,
    isAbleToBeRead: !document.blockSignerAccess,
    isHidden: document.isHidden,
    requester: {
      email: requester.contact || '',
      name: requester.name || '',
    },
    finishedAt: document.finishedAt,
    status: convertStatus(document.status),
    schedule: document.schedule,
    updatedAt: document.updatedAt,
    signings: document.signings.map(signingMapper),
    pending: document.abort && abortToPending(document.abort, participants),
    participants: participants
      .filter((p): p is WithId<SignerWithSigningProperties> => p.type === 'SIGNER')
      .map<Participant>((participant) => {
        return {
          participantId: participant.id,
          name: participant.name || '',
          signingExpiryDate: {
            date: participant.signingPeriod?.to || null,
            // document-model 개선 후 백엔드에서 서명자의 만료 여부를 보내지 않아 클라이언트에서 계산합니다.
            // FYR: https://modusign.slack.com/archives/C03334LUVM4/p1664264141634099?thread_ts=1664258711.604729&cid=C03334LUVM4
            isValid: participant.signingPeriod
              ? moment().isBefore(participant.signingPeriod.to)
              : true,
          },
          signingMethod: {
            methodType: convertSigningMethod(participant.signingMethod as TransportSigningMethod),
            contactInfo: participant.contact || '',
          },
          locale: toLocaleCode(participant.locale),
          signingOrder: participant.order,
          host: participant.host
            ? {
                participantId: participant.host.id,
                dataLabel: participant.host.dataLabel,
              }
            : undefined,
        };
      }),
    hosts,
  };
};
